import React from 'react';
import { TFunction } from 'react-i18next';
import { connect, ConnectedProps } from 'react-redux';
import {
  view,
  errorBoundary,
  withInject,
} from '@piwikpro/platform';
import { RouterService } from '@piwikpro/router-crate';
import {
  Form,
  TextFieldControl,
  ModalView,
  Stack,
  withTranslation,
} from '@piwikpro/ui-components';
import { Apps } from '@piwikpro/apps-crate';
import { FormValidator } from '@piwikpro/form-crate';
import { AdministrationState, STATE_ROOT } from '../../../../redux';

const mapStateToProps = (state: AdministrationState) => {
  const { settings } = state[STATE_ROOT].apps.details.settings;

  return {
    app: settings as NonNullable<typeof settings>,
    filters: state[STATE_ROOT].apps.list.filters,
  };
};

const connector = connect(mapStateToProps);

type ReduxProps = ConnectedProps<typeof connector>;

interface InjectedProps {
  apps: Apps
  validation: FormValidator
  Title: any
  routerService: RouterService
}

interface Props extends ReduxProps, InjectedProps {
  t: TFunction
}

class Rename extends React.Component<Props> {
  constructor(props) {
    super(props);

    this.handleCancel = this.handleCancel.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
  }

  handleCancel() {
    const { routerService } = this.props;

    routerService.dismissAdministrationOverlay();
  }

  async handleSubmit(values, {
    setErrors,
    setSubmitting,
    setFieldValue,
  }) {
    const {
      routerService,
      apps,
      app,
      filters,
    } = this.props;
    const name = values.name.trim();

    try {
      if (name.length > 0) {
        await apps.update(app.id, { name });

        routerService.dismissAdministrationOverlay();
        await apps.get(app.id);
        await apps.find({ ...filters, offset: undefined });
      } else {
        setSubmitting(false);
        setFieldValue('name', name, true);
      }
    } catch (err: any) {
      setErrors(err.errors);
      setSubmitting(false);
    }
  }

  render() {
    const {
      validation,
      t,
      app,
      Title,
    } = this.props;

    return (
      <Title part={t('apps.rename.app.title', { appName: app.attributes.name })} subset>
        <Form
          id="rename-app-form"
          initialValues={{
            name: app.attributes.name,
          }}
          validationSchema={validation.createValidationSchema(validators => ({
            name: validators.apps.name,
          }))}
          onSubmit={this.handleSubmit}
        >
          <ModalView
            title={t('apps.rename.title', { appName: app.attributes.name })}
            onClose={this.handleCancel}
            actions={(
              <Stack spacing="narrow" horizontalAlignment="right" verticalAlignment="center">
                <Form.Cancel onClick={this.handleCancel} />
                <Form.Submit />
              </Stack>
            )}
          >
            <TextFieldControl
              label={t('apps.rename.fields.name')}
              name="name"
              required
              autoFocus
            />
          </ModalView>
        </Form>
      </Title>
    );
  }
}

export default connector(withInject<InjectedProps>({
  apps: 'AppsCrate.apps',
  validation: 'FormCrate.validation',
  Title: 'TitleCrate.components.Title',
  routerService: 'RouterCrate.router',
})(
  view({ role: 'USER', level: 'edit' })(withTranslation(['administration'])(
    errorBoundary(Rename as any),
  )),
));
